import {
  authOriginUIDs,
  purchaseSeatsTabUIDs,
} from "@/global/config/constants";
import {
  gameFiltersState,
} from "@/global/store/data/gameFilters";
import { routes } from "@/global/routes";
import { useCartHelpers } from "../useCartHelpers";
import { selectUser } from "@/global/store/data/user";
import { useApiRoutes } from "@/global/hooks/useApiRoutes";
import { parseApiErrorMessage } from "@/global/utils/utils";
import { useTeamConfig } from "@/global/hooks/useTeamConfig";
import { useAppDispatch, useAppStore } from "@/global/store";
import { useChooseSeats } from "../ChooseSeats/useChooseSeats";
import { useGenericModal } from "@/shared/common/GenericModal";
import { useGenericOAuth } from "@/global/hooks/useGenericOAuth";
import { useInventoryPreviewModal } from "./InventoryPreviewModal";
import { useRouterExtended } from "@/global/hooks/useRouterExtended";
import { eventsState } from "@/global/store/data/purchaseSeats/events";
import { useSelectActiveEvent } from "../ChooseSeats/useSelectActiveEvent";
import { selectProducts } from "@/global/store/data/purchaseSeats/cartManager";

import type { EventType, SectionName } from "@/global/store/data/types";

export function useInventoryPreview() {
  const dispatch = useAppDispatch();

  const router = useRouterExtended();
  const previewModal = useInventoryPreviewModal();
  const { teamSchema, inventoryType, purchaseSeatConfigs } = useTeamConfig();
  let previewAccountGroup = purchaseSeatConfigs?.invPreviewAccountGroup; // prettier-ignore
  const { getInventoryPreviewApi, getEventsApi } = useApiRoutes();
  const previewEvent = previewModal?.event;
  const { activeEvent } = useSelectActiveEvent();
  const user = useAppStore(selectUser);
  const cartProducts = useAppStore(selectProducts(previewEvent?.eventCode));
  const selectedProducts = useAppStore(selectProducts(previewEvent?.eventCode));
  const { connectOAuth } = useGenericOAuth();
  const { handleInventorySelect } = useChooseSeats();
  const { handleManageCart } = useCartHelpers();
  const errorModal = useGenericModal();

  const setSectionFilters = (sectionNames: SectionName[]) => {
    dispatch(
      gameFiltersState.actions.updateInventoryFilters({
        sections: { data: sectionNames, selected: null },
      })
    );
  };

  const fireErrorModal = (error: any) => {
    errorModal.set({
      isOpen: true,
      title: "Error",
      description:
        typeof error === "string" ? error : parseApiErrorMessage(error),
      isCloseButtonHidden: true,
      confirmButtonText: "Close",
    });
  };

  async function handleGetEvents() {
    const { data, error } = await getEventsApi.post({
      teamSchema,
      eventFilters: {
        dates: [],
        eventCodes: [],
        opponentNames: [],
        categories: [],
      },
    });
    if (error) {
      fireErrorModal(parseApiErrorMessage(error));
      return [];
    }
    return data;
  }

  async function handleGetPreviewInventory(event: EventType | null) {
    if (!event || previewAccountGroup === undefined) return;
    if (previewModal?.inventories?.length) return;
    previewModal.set({ isInventoryLoading: true });
    const { data, error } = await getInventoryPreviewApi.post({
      teamSchema,
      eventCode: event?.eventCode,
      inventoryType: inventoryType,
      accountGroupIds: previewAccountGroup,
      inventoryFilters: { sectionNames: [] },
    });

    if (error) {
      console.log(error);
      fireErrorModal(parseApiErrorMessage(error));
      return [];
    }
    setSectionFilters(data?.sectionNames);
    previewModal.set({
      inventories: data?.inventory,
      isInventoryLoading: false,
    });
    return data?.inventory;
  }

  // checkout
  async function handleInitCheckout() {
    if (!previewModal?.event) return;
    previewModal.set({ isCheckoutLoading: true });
    if (user) {
      dispatch(eventsState.actions.setEventsSelected([previewModal?.event]));
      await router.pushSchema({
        pathname: routes.purchaseSeats,
        query: {
          purchaseSeatTab: purchaseSeatsTabUIDs.chooseSeats,
          isPreviewCheckout: "1",
        },
      });
    } else {
      const { error } = await connectOAuth({
        callback: {
          authOrigin: authOriginUIDs.previewInventory,
          pathname: routes.purchaseSeats,
          query: {
            purchaseSeatTab: purchaseSeatsTabUIDs.chooseSeats,
            eventCodes: [previewModal?.event?.eventCode],
            productIds: selectedProducts.map((itm) => itm.id),
            isPreviewCheckout: "1",
          },
        },
      });
      if (error) fireErrorModal(parseApiErrorMessage(error));
    }
  }

  const handleCompleteCheckout = async () => {
    if (!activeEvent || !cartProducts.length) return;
    const onSeatHoldFailed = async (args: any) => {
      if (args?.errorMessage) fireErrorModal(args?.errorMessage);
      previewModal.close();
    };
    await handleManageCart({ onSeatHoldFailed });
    previewModal.close();
  };

  const onCompleteAuth = async (ev: any) => {
    const isPreviewAuthOrigin = ev?.detail?.authOrigin === authOriginUIDs.previewInventory; // prettier-ignore
    const productIds = ev?.detail?.query?.productIds;
    const eventCodes = ev?.detail?.query?.eventCodes;
    if (!isPreviewAuthOrigin || !eventCodes?.length) return;

    const events = await handleGetEvents();
    const event = events?.find((evt) => eventCodes.includes(evt.eventCode));
    if (!event) return;

    // URL-based auto-open: no products selected yet, just open preview
    if (!productIds?.length) {
      previewModal.open({ event });
      return;
    }

    // Checkout flow: user had selected products before auth
    previewModal.open({ event, isCheckoutLoading: true });
    const products = await handleGetPreviewInventory(event);
    const selectedInv = products?.find((inv) => productIds.includes(inv.id)); // prettier-ignore
    if (selectedInv) handleInventorySelect(selectedInv, event);
  };

  return {
    handleInitCheckout,
    handleGetPreviewInventory,
    handleCompleteCheckout,
    onCompleteAuth,
  };
}
